import { useEffect, useState } from 'react';

interface Node {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
}

interface Connection {
  from: number;
  to: number;
}

export function EnterpriseBackground() {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);

  useEffect(() => {
    // Generate network nodes (representing enterprise infrastructure)
    const newNodes: Node[] = [
      { id: 0, x: 50, y: 30, size: 12, delay: 0 }, // Central hub
      { id: 1, x: 25, y: 20, size: 8, delay: 0.2 },
      { id: 2, x: 75, y: 20, size: 8, delay: 0.3 },
      { id: 3, x: 20, y: 50, size: 8, delay: 0.4 },
      { id: 4, x: 80, y: 50, size: 8, delay: 0.5 },
      { id: 5, x: 30, y: 75, size: 8, delay: 0.6 },
      { id: 6, x: 70, y: 75, size: 8, delay: 0.7 },
      { id: 7, x: 10, y: 35, size: 6, delay: 0.8 },
      { id: 8, x: 90, y: 35, size: 6, delay: 0.9 },
      { id: 9, x: 15, y: 65, size: 6, delay: 1.0 },
      { id: 10, x: 85, y: 65, size: 6, delay: 1.1 },
      { id: 11, x: 40, y: 90, size: 6, delay: 1.2 },
      { id: 12, x: 60, y: 90, size: 6, delay: 1.3 },
    ];
    setNodes(newNodes);

    // Generate connections (mesh network)
    const newConnections: Connection[] = [
      { from: 0, to: 1 }, { from: 0, to: 2 }, { from: 0, to: 3 }, { from: 0, to: 4 },
      { from: 0, to: 5 }, { from: 0, to: 6 },
      { from: 1, to: 7 }, { from: 1, to: 3 },
      { from: 2, to: 8 }, { from: 2, to: 4 },
      { from: 3, to: 9 }, { from: 3, to: 5 },
      { from: 4, to: 10 }, { from: 4, to: 6 },
      { from: 5, to: 11 }, { from: 5, to: 9 },
      { from: 6, to: 12 }, { from: 6, to: 10 },
      { from: 7, to: 9 },
      { from: 8, to: 10 },
      { from: 11, to: 12 },
    ];
    setConnections(newConnections);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-purple-900/20 to-slate-900/50" />

      {/* Animated gradient mesh */}
      <div className="absolute inset-0">
        <div
          className="absolute w-[600px] h-[600px] rounded-full"
          style={{
            left: '20%',
            top: '10%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 60%)',
            animation: 'meshFloat 15s ease-in-out infinite',
          }}
        />
        <div
          className="absolute w-[500px] h-[500px] rounded-full"
          style={{
            right: '10%',
            top: '40%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 60%)',
            animation: 'meshFloat 12s ease-in-out infinite 3s',
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full"
          style={{
            left: '40%',
            bottom: '10%',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, transparent 60%)',
            animation: 'meshFloat 10s ease-in-out infinite 5s',
          }}
        />
      </div>

      {/* Network visualization */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="enterpriseLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.4)" />
            <stop offset="50%" stopColor="rgba(139, 92, 246, 0.7)" />
            <stop offset="100%" stopColor="rgba(139, 92, 246, 0.4)" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Connection lines */}
        {connections.map((conn, i) => {
          const fromNode = nodes.find(n => n.id === conn.from);
          const toNode = nodes.find(n => n.id === conn.to);
          if (!fromNode || !toNode) return null;

          return (
            <g key={i}>
              <line
                x1={`${fromNode.x}%`}
                y1={`${fromNode.y}%`}
                x2={`${toNode.x}%`}
                y2={`${toNode.y}%`}
                stroke="url(#enterpriseLineGradient)"
                strokeWidth="1"
                opacity="0.4"
                style={{
                  animation: `lineAppear 2s ease-out ${i * 0.1}s forwards`,
                }}
              />
              {/* Data pulse on line */}
              <circle r="3" fill="#8B5CF6" filter="url(#glow)">
                <animateMotion
                  dur={`${3 + i * 0.3}s`}
                  repeatCount="indefinite"
                  begin={`${i * 0.5}s`}
                >
                  <mpath xlinkHref={`#path-${i}`} />
                </animateMotion>
              </circle>
              <path
                id={`path-${i}`}
                d={`M${fromNode.x * 10} ${fromNode.y * 5} L${toNode.x * 10} ${toNode.y * 5}`}
                fill="none"
                stroke="none"
              />
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => (
          <g key={node.id}>
            {/* Outer glow */}
            <circle
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r={node.size * 2}
              fill="rgba(139, 92, 246, 0.2)"
              style={{
                animation: `nodeGlow 3s ease-in-out infinite ${node.delay}s`,
              }}
            />
            {/* Inner circle */}
            <circle
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r={node.size}
              fill={node.id === 0 ? '#8B5CF6' : 'rgba(139, 92, 246, 0.8)'}
              filter="url(#glow)"
              style={{
                animation: `nodeAppear 0.5s ease-out ${node.delay}s forwards, nodePulse 2s ease-in-out infinite ${node.delay}s`,
              }}
            />
            {/* Center dot */}
            <circle
              cx={`${node.x}%`}
              cy={`${node.y}%`}
              r={node.size / 3}
              fill="white"
              opacity="0.8"
            />
          </g>
        ))}
      </svg>

      {/* Floating status badges */}
      <div
        className="absolute"
        style={{
          left: '15%',
          top: '45%',
          animation: 'floatBadge 5s ease-in-out infinite',
        }}
      >
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/20 border border-success/30 backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-success font-medium">SOC 2 Compliant</span>
        </div>
      </div>

      <div
        className="absolute"
        style={{
          right: '15%',
          top: '55%',
          animation: 'floatBadge 5s ease-in-out infinite 2s',
        }}
      >
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/20 border border-accent/30 backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          <span className="text-xs text-accent font-medium">Enterprise Ready</span>
        </div>
      </div>

      {/* Hexagon pattern overlay */}
      <svg className="absolute inset-0 w-full h-full opacity-5">
        <defs>
          <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
            <polygon points="25,0 50,14.4 50,43.3 25,57.7 0,43.3 0,14.4" fill="none" stroke="rgba(139, 92, 246, 0.5)" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hexagons)" />
      </svg>

      {/* Animation keyframes */}
      <style>{`
        @keyframes meshFloat {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(30px, -20px) scale(1.05);
          }
        }

        @keyframes lineAppear {
          from {
            opacity: 0;
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
          }
          to {
            opacity: 0.4;
            stroke-dasharray: 100;
            stroke-dashoffset: 0;
          }
        }

        @keyframes nodeAppear {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes nodePulse {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes nodeGlow {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.2);
          }
        }

        @keyframes floatBadge {
          0%, 100% {
            transform: translateY(0);
            opacity: 0.8;
          }
          50% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
